import { z } from 'zod'

/**
 * Source of truth for Task input validation. Frontend forms use these via
 * `zodResolver(taskCreateSchema)`. Backend can either:
 *   - import the same schema and run it with `nestjs-zod`, or
 *   - read it as a spec to write equivalent class-validator rules.
 *
 * Whenever you add/relax a constraint here, both ends update in lockstep —
 * no drift between client-side UX validation and server-side enforcement.
 */

export const TASK_KIND        = ['CALL', 'MEETING', 'EMAIL', 'OTHER']        as const
export const TASK_PRIORITY    = ['LOW', 'MEDIUM', 'HIGH']                    as const
export const TASK_BINDING_KIND = ['DEAL', 'RENTAL', 'PROPERTY', 'CONTACT']   as const

export const taskBindingSchema = z.object({
	kind: z.enum(TASK_BINDING_KIND),
	id:   z.string().min(1),
})

export const taskCreateSchema = z.object({
	kind:          z.enum(TASK_KIND),
	title:         z.string().trim().max(200).optional(),
	description:   z.string().trim().min(1).max(500),
	binding:       taskBindingSchema.optional(),
	responsibleId: z.string().min(1).optional(),
	dueDate:       z.string().datetime({ offset: true }).optional(),
	priority:      z.enum(TASK_PRIORITY).default('MEDIUM'),
})

export const taskUpdateSchema = z.object({
	kind:          z.enum(TASK_KIND).optional(),
	title:         z.string().trim().max(200).optional(),
	description:   z.string().trim().min(1).max(500).optional(),
	binding:       taskBindingSchema.optional(),
	responsibleId: z.string().min(1).optional(),
	dueDate:       z.string().datetime({ offset: true }).optional(),
	priority:      z.enum(TASK_PRIORITY).optional(),
	isDone:        z.boolean().optional(),
})

/**
 * NOTE: these `z.infer` types intentionally mirror the hand-written
 * `CreateTaskBody` / `UpdateTaskBody` from `task.ts`. Keep them in sync.
 * The TypeScript shape is asserted at the API hook boundary (RTK Query),
 * the zod schema enforces runtime constraints in forms.
 */
export type TaskCreateInput = z.infer<typeof taskCreateSchema>
export type TaskUpdateInput = z.infer<typeof taskUpdateSchema>
